import React, {useContext, useMemo, useRef, useState} from "react";
import {Button, Tag, Modal, Switch, message} from "antd";
import {ENV, operationTypeList, REFRESH_TYPE_FUNCTION} from "../../shared/utils/enum";
import {formatMinutes} from "../../shared/utils/dayjsUtil";
import dayjs from "dayjs";
import {Sorter} from "../../shared/components/Sorter";
import EventEditModal from "./EventEditModal";
import TablePage from "../../pages/tablePage/TablePage";
import {useEventTable} from "./useEventTable";
import IsLoading from "../../shared/components/IsLoading";
import ErrorPage from "../../shared/components/ErrorPage";
import {AuthContext} from "../../shared/contexts/authContext";
import toast from "react-hot-toast";
import {internalApi} from "../../common/axios";
import {useReloadTime} from "../serverInfo/useServerInfo";
import {EventEntity} from "./EventEntity";
import {EVENT_CSV_COLUMNS} from "./eventCsvColumns";
import {checkEventCsvRow} from "./eventCsvCheck";

const Event = () => {
    const {currentUser} = useContext(AuthContext);

    const [rowData, setRowData] = useState({});
    const [isChanged, setIsChanged] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [selectRowSeqList, setSelectRowSeqList] = useState([]);

    const refreshRef = useRef(null);
    const {data: eventTable, isLoading, error} = useEventTable();
    const {data: reloadTimeData, isLoading: isReloadTimeData, error: reloadTimeDataError, refetch: reloadTimeDataRefetch} = useReloadTime();


    const handleCopyLiveData = async () => {
        if (currentUser.target === ENV.PRODUCTION) {
            toast.error('운영 환경에서는 라이브 데이터 복사 기능을 사용할 수 없습니다.');
            return;
        }
        const result = await internalApi.put('/events');
        if (result) {
            toast.success('라이브 데이터 복사가 완료되었습니다.');
            if (refreshRef.current) {
                refreshRef.current(REFRESH_TYPE_FUNCTION.GAME_DATA);
            }
        }
    }

    const showCopyConfirm = () => {
        Modal.confirm({
            title: 'Live 데이터 복사',
            content: '정말로 Live 데이터를 복사하시겠습니까?',
            okText: '확인',
            cancelText: '취소',
            onOk: handleCopyLiveData,
        });
    };

    // rowSelection 옵션
    const rowSelection = {
        selectedRowKeys,
        onChange: (selectedRowKeys, selectedRows) => {
            setSelectedRowKeys(selectedRowKeys);
            setSelectRowSeqList(selectedRows.map(row => row.seq));
        }
    };

    // On/Off 스위치 토글 핸들러
    const handleSwitchChange = async (checked, record) => {
        try {
            if (record.end < dayjs().unix()) {
                toast.error('종료된 이벤트는 On/Off 상태를 변경할 수 없습니다.');
                return;
            }
            await internalApi.post('/events/onoff', {
                seq: record.seq,
                onOff: checked ? 1 : 0
            });
            toast.success('On/Off 상태가 변경되었습니다.');
            setIsChanged(record.seq + checked);
        } catch (e) {
            toast.error('On/Off 변경 실패');
        }
    };

    // 선택 복사 함수
    const handleCopySelected = async () => {
        if (selectedRowKeys.length === 0) {
            message.warning('복사할 항목을 선택하세요.');
            return;
        }
        Modal.confirm({
            title: '선택 복사',
            content: '정말로 선택한 항목을 복사하시겠습니까? 복사 시 시작일과 종료일에 1일씩 더해집니다.',
            okText: '확인',
            cancelText: '취소',
            onOk: async () => {
                try {
                    // 실제 삭제 API 호출 (엔드포인트는 서버에 맞게 조정)
                    await internalApi.post('/events/bulk_copy', {seqList: selectRowSeqList});
                    message.success('복사가 완료되었습니다.');
                    setSelectedRowKeys([]);
                    setSelectRowSeqList([]);
                    if (refreshRef.current) {
                        refreshRef.current(REFRESH_TYPE_FUNCTION.GAME_DATA);
                    }
                } catch (e) {
                    message.error('복사 중 오류가 발생했습니다.');
                }
            }
        });
    };
    
    // 선택 삭제 함수
    const handleDeleteSelected = async () => {
        if (selectedRowKeys.length === 0) {
            message.warning('삭제할 항목을 선택하세요.');
            return;
        }
        Modal.confirm({
            title: '선택 삭제',
            content: '정말로 선택한 항목을 삭제하시겠습니까?',
            okText: '확인',
            cancelText: '취소',
            onOk: async () => {
                try {
                    // 실제 삭제 API 호출 (엔드포인트는 서버에 맞게 조정)
                    await internalApi.post('/events/bulk_delete', {seqList: selectRowSeqList});
                    message.success('삭제가 완료되었습니다.');
                    setSelectedRowKeys([]);
                    setSelectRowSeqList([]);
                    if (refreshRef.current) {
                        refreshRef.current(REFRESH_TYPE_FUNCTION.GAME_DATA);
                    }
                } catch (e) {
                    message.error('삭제 중 오류가 발생했습니다.');
                }
            }
        });
    };

    const checkCsvRow = (entity, csvRow) => checkEventCsvRow(entity, csvRow, eventTable);

    const columns = useMemo(() => ([
        {
            title: '시퀀스',
            dataIndex: 'seq',
            key: 'seq',
            search: true,
        },
        {
            title: '이벤트',
            dataIndex: 'typeIndex',
            render: (_, {typeIndex}) => (
                <>
                    {eventTable[typeIndex]?.nameText || ''}({typeIndex}){' '}
                    {eventTable[typeIndex]?.difficultSet === 1 && (
                        <span style={{color: 'green', fontWeight: 'bold'}}> [Easy]</span>
                    )}
                    {eventTable[typeIndex]?.difficultSet === 2 && (
                        <span style={{color: 'red', fontWeight: 'bold'}}> [Hard]</span>
                    )}
                </>
            ),
            key: 'typeIndex',
            // 이름 검색 → 선택한 typeIndex 목록이 서버 필터로 전달됨 (서버는 이름을 모름)
            filters: Object.entries(eventTable || {}).map(([idx, e]) => ({
                text: `${e?.nameText || ''}(${idx})`,
                value: Number(idx),
            })),
            filterSearch: true,
            onFilter: (value, {typeIndex}) => typeIndex === value,
        },
        {
            title: 'On/Off',
            dataIndex: 'onOff',
            render: (_, record) => (
                <Switch
                    checked={record.onOff === 1}
                    checkedChildren="On"
                    unCheckedChildren="Off"
                    onChange={checked => handleSwitchChange(checked, record)}
                    onClick={(_, e) => e.stopPropagation()}
                />
            ),
            filters: [
                {
                    text: 'On',
                    value: 1,
                },
                {
                    text: 'Off',
                    value: 0,
                },
            ],
            onFilter: (value, record) => record.onOff === value,
            key: 'onOff',
        },
        {
            title: '운영 타입',
            dataIndex: 'operationType',
            render: (_, {operationType}) =>{
                const opTypeData = operationTypeList.find(item => item.value === operationType);
                if (opTypeData && opTypeData.label) {
                    return <Tag color={opTypeData.color}>{opTypeData.label}</Tag>;
                }
            },
            key: 'operationType',
        },
        {
            title: '계정 레벨',
            dataIndex: 'openLevel',
            key: 'openLevel',
        },
        {
            title: '최대 레벨',
            dataIndex: 'maxLevel',
            key: 'maxLevel',
        },
        {
            title: '시작 시간',
            dataIndex: 'startTime',
            render: (startTime) => formatMinutes(startTime).format('HH:mm'),
            key: 'startTime',
        },
        {
            title: '깜짝 이벤트',
            dataIndex: 'surpriseTime',
        },
        {
            title: 'Start',
            dataIndex: 'begin',
            render: (begin) => dayjs.unix(begin).format('YYYY-MM-DD HH:mm:ss'),
            key: 'begin',
        },
        {
            title: 'End',
            dataIndex: 'end',
            render: (end) => dayjs.unix(end).format('YYYY-MM-DD HH:mm:ss'),
            sorter: (a, b) => Sorter.DATE(a.end, b.end),
            key: 'end',
        },
        {
            title: '최소 적용 버전',
            dataIndex: 'minVersion',
            key: 'minVersion',
            render: (_, {typeIndex}) => (
                <Tag>{eventTable[typeIndex]?.openVersion || '테이블에 값을 넣어주세요.'}</Tag>
            )
        },
        {
            title: 'Memo',
            dataIndex: 'memo',
            key: 'memo',
        }
    ]), [eventTable]);

    if (isLoading || isReloadTimeData) return <IsLoading/>;
    if (error || reloadTimeDataError) return <ErrorPage error={error}/>;

    const props = {
        columns: columns,
        rowData,
        setRowData,
        modal: EventEditModal,
        refreshRef,
        isChanged,
        url: 'events',
        buttonText: '신규 이벤트 추가',
        csvUpload: 'events/csv',
        csvCompareUrl: 'events?filters={}',
        csvIdentityKey: 'seq',
        csvDeleteMissing: false,
        entityModel: EventEntity,
        csvColumns: EVENT_CSV_COLUMNS,
        csvTemplateName: 'event_master_form.csv',
        csvCheckRow: checkCsvRow,
        csvGuide: [
            '인코딩 : UTF-8(BOM 포함) / 구분자 : 쉼표(,)',
            'Excel 저장 시 [다른 이름으로 저장 > CSV UTF-8(쉼표로 분리)(*.csv)]를 선택해야 한글이 깨지지 않습니다.',
            '날짜 : YYYY-MM-DD HH:mm:ss 또는 unix(초) 둘 다 사용 가능합니다.',
            'seq : 비워두면 신규 등록, 값이 있으면 해당 행 수정입니다.',
            'typeIndex 는 Event_Setting.csv 에 있는 이벤트여야 하며, eventType / loadTableIdx / rewardGroup 은 비워두면 Event_Setting.csv 값으로 채워집니다.',
            '같은 eventType 끼리 운영 기간이 겹치면 저장되지 않습니다. (기존 등록분 포함)',
            'onOff / operationType / startTime 처럼 범위가 정해진 값이 벗어나면 기본값으로 자동 보정하고 내역을 보여줍니다.',
            'typeIndex 와 시작/종료 일시는 자동 보정하지 않습니다. 잘못되면 해당 행을 고쳐서 다시 올려주세요.',
            '[CSV 양식 다운로드] 로 빈 양식을 받아 작성하세요. 2번째 # 행은 설명이며 업로드 시 무시됩니다.',
        ],
        refreshTypeFunc: REFRESH_TYPE_FUNCTION.GAME_DATA,
        reloadTime: reloadTimeData?.game_data_sync_id,
        reloadTimeRefetch: reloadTimeDataRefetch
    }

    return (
        <div>
            {
                currentUser.target !== ENV.PRODUCTION && (
                    <Button
                        style={{marginBottom: 16, marginRight: 16}}
                        onClick={showCopyConfirm}
                    >
                        Live 데이터 복사하기
                    </Button>
                )
            }
            <Button
                style={{marginBottom: 16, marginRight: 16}}
                onClick={handleCopySelected}
            >
                선택 데이터 복사하기
            </Button>
            <Button
                style={{marginBottom: 16, marginRight: 16}}
                onClick={handleDeleteSelected}
            >
                선택 데이터 삭제하기
            </Button>
            <TablePage {...props} rowSelection={rowSelection}/>
        </div>
    );
};

export default Event;